import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Grid, Typography } from '@mui/material';

import AuthorizerTypeField from './AuthorizerTypeField';
import ApiKeyKeysEditor from './ApiKeyKeysEditor';
import OidcFields from './OidcFields';
import cleanEmptyValues from '@/util/cleanEmptyValues';
import { AUTHORZIER_TYPES } from './utils';

function resolveInitialType(initialValues, allow_none) {
  const type = initialValues?.type;
  if (type === AUTHORZIER_TYPES.API_KEY || type === AUTHORZIER_TYPES.OIDC) {
    return type;
  }
  return allow_none ? AUTHORZIER_TYPES.NONE : '';
}

export default function AuthorizerFields({
  initialValues,
  onChange,
  edit = false,
  stacked = false,
  allowNone = true,
  typeVariant = 'select',
  lockType = false,
  showHeading = true,
}) {
  const initial_type = resolveInitialType(initialValues, allowNone);

  const [type, setType] = useState(initial_type);
  const [api_keys, setApiKeys] = useState(
    Array.isArray(initialValues?.keys) ? initialValues.keys : [],
  );
  const [oidc, setOidc] = useState(initialValues?.oidc || {});

  const [api_key_error, setApiKeyError] = useState(false);
  const [oidc_error, setOidcError] = useState(false);

  const isInitialMount = useRef(true);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const payload = useMemo(() => {
    if (type === AUTHORZIER_TYPES.NONE) return undefined;
    if (type === AUTHORZIER_TYPES.API_KEY) {
      return cleanEmptyValues({
        type,
        keys: api_keys,
      });
    }
    if (type === AUTHORZIER_TYPES.OIDC) {
      return cleanEmptyValues({
        type,
        oidc,
      });
    }
    return undefined;
  }, [type, api_keys, oidc]);

  const type_missing =
    !allowNone &&
    type !== AUTHORZIER_TYPES.API_KEY &&
    type !== AUTHORZIER_TYPES.OIDC;

  const has_error =
    type_missing ||
    (type === AUTHORZIER_TYPES.API_KEY && api_key_error) ||
    (type === AUTHORZIER_TYPES.OIDC && oidc_error);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      onChangeRef.current(payload, has_error);
      return;
    }
    onChangeRef.current(payload, has_error);
  }, [payload, has_error]);

  return (
    <Grid container spacing={2}>
      {showHeading ? (
        <Grid size={12}>
          <Typography variant='body1' fontWeight='medium' sx={{ mb: 1 }}>
            Authorization
          </Typography>
        </Grid>
      ) : null}

      <Grid size={12}>
        <AuthorizerTypeField
          value={type}
          onChange={(new_type) => {
            setType(new_type);
            setApiKeyError(false);
            setOidcError(false);
          }}
          disabled={edit || lockType}
          variant={typeVariant}
        />
      </Grid>

      {type === AUTHORZIER_TYPES.API_KEY ? (
        <Grid size={12}>
          <ApiKeyKeysEditor
            value={api_keys}
            onChange={(next_value, error) => {
              setApiKeys(next_value);
              setApiKeyError(error);
            }}
            disabled={edit}
            allowNone={allowNone}
            stacked={stacked}
          />
        </Grid>
      ) : null}

      {type === AUTHORZIER_TYPES.OIDC ? (
        <Grid size={12}>
          <OidcFields
            value={oidc}
            onChange={(next_value, error) => {
              setOidc(next_value);
              setOidcError(error);
            }}
            disabled={edit}
            stacked={stacked}
          />
        </Grid>
      ) : null}
    </Grid>
  );
}

